import React, { useState, useEffect } from 'react';
import BasicModal from '../../newStudent/Modal/BasicModal';
import { IGroupParticipant, IChatUser } from '../../../interfaces/chat/chat.interface';
import chatService from '../../../services/chat/chat.service';
import { HiUserGroup, HiPlus, HiTrash } from 'react-icons/hi';
import * as S from './styles';
import ParticipantSelectorModal from '../ParticipantSelectorModal';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import { useLoading } from '../../agent/Context/LoadingContext';
import { ButtonSimple } from '../../newStudent/Buttons';
import HeaderModal from '../../agent/HeaderModal';

interface IGroupParticipantsModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: number;
  currentUserId: number;
  users: IChatUser[];
  onUpdate?: () => void;
}

export default function GroupParticipantsModal({
  isOpen,
  onClose,
  conversationId,
  currentUserId,
  users: initialUsers,
  onUpdate,
}: IGroupParticipantsModalProps) {
  const { setIsLoading } = useLoading();
  const [participants, setParticipants] = useState<IGroupParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [participantToRemove, setParticipantToRemove] = useState<number | null>(null);
  const [users, setUsers] = useState<IChatUser[]>(initialUsers);

  useEffect(() => {
    if (isOpen && conversationId) {
      loadParticipants();
    }
  }, [isOpen, conversationId]);

  useEffect(() => {
    if (showAddModal) {
      loadUsers();
    }
  }, [showAddModal]);

  const loadParticipants = async () => {
    setLoading(true);
    setIsLoading(true);
    try {
      const data = await chatService.getGroupParticipants(conversationId);
      setParticipants(data);
      const currentUserParticipant = data.find((p) => p.id === currentUserId);
      setIsAdmin(currentUserParticipant?.isAdmin || false);
    } catch (error) {
      // Erro ao carregar participantes
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const allUsers = await chatService.getAllUsers();
      setUsers(allUsers);
    } catch (error) {
      // Erro ao carregar usuários
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddParticipants = async (participantIds: number[]) => {
    setIsLoading(true);
    try {
      const success = await chatService.addGroupParticipants(conversationId, participantIds);
      if (success) {
        await loadParticipants();
        onUpdate?.();
        setShowAddModal(false);
      }
    } catch (error) {
      // Erro ao adicionar participantes
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveClick = (participantId: number) => {
    setParticipantToRemove(participantId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmRemove = async () => {
    if (!participantToRemove) return;

    setIsLoading(true);
    setParticipants((prev) => prev.filter((p) => p.id !== participantToRemove));

    try {
      const success = await chatService.removeGroupParticipant(conversationId, participantToRemove);
      if (success) {
        await loadParticipants();
        onUpdate?.();
        if (participantToRemove === currentUserId) {
          onClose();
        }
      } else {
        await loadParticipants();
      }
      setShowDeleteConfirm(false);
      setParticipantToRemove(null);
    } catch (error) {
      await loadParticipants();
      setShowDeleteConfirm(false);
      setParticipantToRemove(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelRemove = () => {
    setShowDeleteConfirm(false);
    setParticipantToRemove(null);
  };

  const canRemove = (participant: IGroupParticipant) => {
    if (participant.id === currentUserId && !participant.isAdmin) {
      return true;
    }
    if (isAdmin && participant.id !== currentUserId) {
      return true;
    }
    return false;
  };

  const availableUsers = users.filter(
    (user) => !participants.some((p) => p.id === user.id) && user.id !== currentUserId,
  );

  return (
    <>
      <BasicModal
        isOpen={isOpen}
        setIsOpen={() => onClose()}
        handleOnClose={onClose}
        isFloat
        zIndex={1100}
      >
        <S.Container>
          <HeaderModal
            setOpen={onClose}
            title="Participantes do Grupo"
            subtitle
            icon={<HiUserGroup size={24} />}
            handleClose={() => onClose()}
          />
          <S.Content>
            {loading ? (
              <S.Loading>Carregando participantes...</S.Loading>
            ) : (
              <>
                {isAdmin && (
                  <ButtonSimple color="primary" font="p" widthSize="fullWidth" heightSize="small" startIcon={<HiPlus size={18} />} onClick={() => setShowAddModal(true)}>
                    Adicionar Participantes
                  </ButtonSimple>
                )}

                <S.ParticipantsList>
                  {participants.length === 0 ? (
                    <S.EmptyState>
                      <S.EmptyText>Nenhum participante encontrado</S.EmptyText>
                    </S.EmptyState>
                  ) : (
                    participants.map((participant) => (
                      <S.ParticipantItem key={participant.id}>
                        <S.AvatarContainer>
                          {participant.avatar ? (
                            <S.Avatar src={participant.avatar} alt={participant.name} />
                          ) : (
                            <S.AvatarPlaceholder>
                              {participant.name.charAt(0).toUpperCase()}
                            </S.AvatarPlaceholder>
                          )}
                        </S.AvatarContainer>
                        <S.ParticipantInfo>
                          <S.ParticipantName>
                            {participant.name}
                            {participant.isAdmin && <S.AdminBadge>Admin</S.AdminBadge>}
                          </S.ParticipantName>
                        </S.ParticipantInfo>
                        {canRemove(participant) && (
                          <S.RemoveButton
                            onClick={() => handleRemoveClick(participant.id)}
                            title="Remover participante"
                          >
                            <HiTrash size={18} />
                          </S.RemoveButton>
                        )}
                      </S.ParticipantItem>
                    ))
                  )}
                </S.ParticipantsList>
              </>
            )}
          </S.Content>
        </S.Container>
      </BasicModal>

      {showAddModal && (
        <ParticipantSelectorModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          users={availableUsers}
          onSelectParticipants={handleAddParticipants}
        />
      )}

      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        message="Tem certeza que deseja remover este participante do grupo?"
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />
    </>
  );
}

